// Shared state for the Nurse Dashboard. The Shell builds the value (patient
// census, current actor, navigation, reloads, date range) and provides it;
// every view consumes it via useNurseData().
import { createContext, useContext } from "react";
import { istDayKey } from "../../utils/istDateTime";

export const NurseDataContext = createContext(null);

export function useNurseData() {
  const ctx = useContext(NurseDataContext);
  if (!ctx) throw new Error("useNurseData must be used within the Nurse Dashboard shell");
  return ctx;
}

export function dateFilterParams(dateFrom, dateTo) {
  const params = {};
  if (dateFrom) params.from_date = dateFrom;
  if (dateTo) params.to_date = dateTo;
  return params;
}

export function computeAge(dob) {
  if (!dob || typeof dob !== "string") return null;
  const birth = new Date(dob);
  if (Number.isNaN(birth.getTime())) {
    const year = Number(dob.split("-")[0]);
    return Number.isFinite(year) ? new Date().getFullYear() - year : null;
  }
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

// Normalize one ipd_admission/ row into the patient shape the views expect.
export function normalizeAdmission(item = {}) {
  const patient = item.patient || {};
  const admitting = item.doctors?.admitting || {};
  return {
    id: String(item.admission_id ?? item.id ?? ""),
    admissionId: item.admission_id ?? item.id,
    status: item.status,
    patientId: patient.patient_id || "",
    uhid: patient.uhid || patient.patient_id || "",
    name: patient.name || [patient.first_name, patient.last_name].filter(Boolean).join(" ") || "Unknown",
    gender: patient.gender || "",
    age: computeAge(patient.dob),
    ward: item.location || "",
    // The ward is the bed type; the admission payload no longer carries a
    // ward_name, which held a main_ipd_ward (department) name.
    wardName: item.bed_type_name || "",
    bed: item.bed_id || "",
    room: "",
    bedTypeName: item.bed_type_name || "",
    departmentId: item.department_id || "",
    consultant: admitting.name || item.doctor_name || "",
    // Treating doctor's id — default prescriber for nurse-entered (on-behalf)
    // medication orders (CPOE D2).
    consultantId: admitting.doctor_id || admitting.id || item.assigned_doctor_id || "",
    admissionDate: istDayKey(item.admitted_at),
    diagnosis: item.admission_details?.diagnosis || "",
    careContextRef: item.admission_details?.care_context_ref || "",
    // Filled in lazily by the patient-detail screen:
    vitals: {},
    fallRisk: null,
    painScore: null,
    pressureUlcerRisk: null,
    allergies: [],
  };
}

// Map the latest assessment + latest vitals onto a patient object.
export function applyClinical(patient, assessment, latestVital) {
  return {
    ...patient,
    fallRisk: assessment?.fall_risk ?? patient.fallRisk,
    painScore: assessment?.pain_score ?? patient.painScore,
    pressureUlcerRisk: assessment?.pressure_ulcer_risk ?? patient.pressureUlcerRisk,
    allergies: assessment?.allergies ?? patient.allergies ?? [],
    vitals: latestVital
      ? {
          bloodPressure: latestVital.blood_pressure,
          heartRate: latestVital.heart_rate,
          temperature: latestVital.temperature,
          oxygenSaturation: latestVital.oxygen_saturation,
          respiratoryRate: latestVital.respiratory_rate,
          weight: latestVital.weight,
          heightCm: latestVital.height_cm,
          cvp: latestVital.cvp_cmh2o,
          o2Mode: latestVital.o2_mode,
          o2Flow: latestVital.o2_flow_lpm,
          lastUpdated: latestVital.recorded_at,
        }
      : patient.vitals,
  };
}
